import React, { useState, useEffect } from 'react';
import { useSearchParams, useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '../utils/supabaseClient';
import SEO from './SEO';
import { getSEOConfig } from '../utils/seoConfig';

const ArtistPublicPage = () => {
  const [searchParams] = useSearchParams();
  const { handle: paramHandle } = useParams();
  const navigate = useNavigate();
  
  // Support both query param (?handle=artist-name) and URL param (/artist/artist-name)
  // Also support FlutterFlow legacy parameters
  const handle = paramHandle || searchParams.get('handle') || searchParams.get('artist_handle');
  
  const [artist, setArtist] = useState(null);
  const [artworks, setArtworks] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchArtistData = async () => {
      if (!handle) {
        setError('No artist handle provided');
        setLoading(false);
        return;
      }

      try {
        // Fetch artist details with enhanced query
        const { data: artistData, error: artistError } = await supabase
          .from('artists')
          .select('*')
          .eq('handle', handle)
          .eq('is_public', true)
          .single();

        if (artistError) {
          // Try alternative table names if main query fails
          const { data: altArtistData, error: altError } = await supabase
            .from('artist')
            .select('*')
            .eq('handle', handle)
            .single();

          if (altError) throw artistError;
          setArtist(altArtistData);
        } else {
          setArtist(artistData);
        }

        // Fetch artist's artworks
        const { data: artworksData, error: artworksError } = await supabase
          .from('artworks')
          .select('*')
          .eq('artist_id', artistData?.id || altArtistData?.id)
          .eq('is_public', true)
          .order('created_at', { ascending: false })
          .limit(12);

        if (artworksError) {
          console.error('Error fetching artworks:', artworksError);
          // Don't throw here, just log - artist page should still show
        }

        setArtworks(artworksData || []);

        // Fetch artist's collections
        const { data: collectionsData, error: collectionsError } = await supabase
          .from('collections')
          .select('*')
          .eq('artist_id', artistData?.id || altArtistData?.id)
          .eq('is_public', true)
          .order('created_at', { ascending: false })
          .limit(6);

        if (collectionsError) {
          console.error('Error fetching collections:', collectionsError);
        }

        setCollections(collectionsData || []);
      } catch (err) {
        console.error('Error fetching artist:', err);
        setError('Failed to load artist profile');
      } finally {
        setLoading(false);
      }
    };

    fetchArtistData();
  }, [handle]);

  // Handle legacy FlutterFlow route redirects
  useEffect(() => {
    if (handle && !loading && !error && artist) {
      // If we're on the legacy route, we could optionally redirect to the modern route
      // For now, we'll keep both working to preserve SEO
      const currentPath = window.location.pathname;
      if (currentPath === '/artist') {
        // Optionally set canonical URL for SEO
        const canonicalLink = document.createElement('link');
        canonicalLink.rel = 'canonical';
        canonicalLink.href = `${window.location.origin}/artist/${handle}`;
        document.head.appendChild(canonicalLink);
      }
    }
  }, [handle, loading, error, artist]);

  if (loading) {
    return (
      <div className="artist-public-loading">
        <div className="loading-spinner">Loading artist profile...</div>
      </div>
    );
  }

  if (error || !artist) {
    return (
      <div className="artist-public-error">
        <h1>Artist Not Found</h1>
        <p>{error || 'The artist profile you\'re looking for doesn\'t exist.'}</p>
        <button 
          onClick={() => navigate('/')}
          className="btn btn-primary"
        >
          Return to Home
        </button>
      </div>
    );
  }

  return (
    <div className="artist-public-page">
      <SEO {...getSEOConfig.artist(artist)} />

      {/* Artist Hero Section */}
      <div className="artist-hero">
        <div className="artist-cover-image">
          {artist.cover_image && (
            <img 
              src={artist.cover_image} 
              alt={`${artist.full_name} cover`}
              className="cover-image"
            />
          )}
        </div>
        
        <div className="artist-profile-header">
          <div className="artist-avatar-container">
            {artist.profile_image && (
              <img 
                src={artist.profile_image} 
                alt={artist.full_name}
                className="artist-avatar"
              />
            )}
          </div>
          
          <div className="artist-info">
            <h1 className="artist-name">{artist.full_name}</h1>
            {artist.location && (
              <p className="artist-location">{artist.location}</p>
            )}
            {artist.bio && (
              <p className="artist-bio">{artist.bio}</p>
            )}
            
            {/* Artist social links */}
            {(artist.website || artist.instagram || artist.facebook || artist.twitter) && (
              <div className="artist-social">
                {artist.website && (
                  <a href={artist.website} target="_blank" rel="noopener noreferrer" className="social-link">
                    Website
                  </a>
                )}
                {artist.instagram && (
                  <a href={`https://instagram.com/${artist.instagram}`} target="_blank" rel="noopener noreferrer" className="social-link">
                    Instagram
                  </a>
                )}
                {artist.facebook && (
                  <a href={`https://facebook.com/${artist.facebook}`} target="_blank" rel="noopener noreferrer" className="social-link">
                    Facebook
                  </a>
                )}
                {artist.twitter && (
                  <a href={`https://twitter.com/${artist.twitter}`} target="_blank" rel="noopener noreferrer" className="social-link">
                    Twitter
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Artist Collections */}
      {collections.length > 0 && (
        <div className="artist-collections">
          <h2>Collections</h2>
          <div className="collections-grid">
            {collections.map((collection) => (
              <div key={collection.id} className="collection-card">
                {collection.cover_image && (
                  <img 
                    src={collection.cover_image} 
                    alt={collection.title}
                    className="collection-cover"
                  />
                )}
                <div className="collection-info">
                  <h3>{collection.title}</h3>
                  {collection.description && (
                    <p>{collection.description}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Artist Artworks */}
      {artworks.length > 0 && (
        <div className="artist-artworks">
          <h2>Artworks</h2>
          <div className="artworks-grid">
            {artworks.map((artwork) => (
              <div key={artwork.id} className="artwork-card">
                <Link to={`/feelArtStory?artId=${artwork.id}`} className="artwork-link">
                  <img 
                    src={artwork.primary_image || artwork.image_url} 
                    alt={artwork.title}
                    className="artwork-image"
                  />
                  <div className="artwork-info">
                    <h3>{artwork.title}</h3>
                    {artwork.medium && <p className="medium">{artwork.medium}</p>}
                    {artwork.dimensions && <p className="dimensions">{artwork.dimensions}</p>}
                  </div>
                </Link>
              </div>
            ))}
          </div>
          
          {artworks.length >= 12 && (
            <div className="view-more">
              <Link to={`/artist/${handle}/artworks`} className="view-more-link">
                View All Artworks →
              </Link>
            </div>
          )}
        </div>
      )}

      {/* Artist Statement */}
      {artist.artist_statement && (
        <div className="artist-statement">
          <h2>Artist Statement</h2>
          <p>{artist.artist_statement}</p>
        </div>
      )}

      {/* Contact Information */}
      {artist.email && (
        <div className="artist-contact">
          <h2>Contact</h2>
          <p>Interested in this artist's work? <a href={`mailto:${artist.email}`}>Send an inquiry</a></p>
        </div>
      )}
    </div>
  );
};

export default ArtistPublicPage;
